import React, { useEffect, useState } from "react";
import { API } from "@/api";
import { Modal } from "@/Components/Common/Tailwind";
import { Skeleton } from "@/Components/ui/skeleton";
import { ChevronLeft } from "lucide-react";
import type { DisplayMenu } from "@/api/type";

export function useDisplayMenus(siteId: number | string, enabled = true) {
    const [displayMenus, setDisplayMenus] = useState<DisplayMenu[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!enabled) {
            setDisplayMenus([]);
            setIsLoading(false);
            return;
        }

        let isMounted = true;
        setIsLoading(true);

        API.get(`/sites/display-menus/?site=${siteId}`)
            .then((res) => {
                if (!isMounted) {
                    return;
                }
                const data = res.data;
                const list = Array.isArray(data) ? data : (data?.results || []);
                setDisplayMenus(list);
            })
            .catch((error) => {
                console.error(error);
                if (isMounted) {
                    setDisplayMenus([]);
                }
            })
            .finally(() => {
                if (isMounted) {
                    setIsLoading(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [siteId, enabled]);

    return { displayMenus, isLoading };
}

interface DisplayMenuCardProps {
    displayMenu: DisplayMenu;
    onClick: () => void;
    className?: string;
}

export function DisplayMenuCard({
    displayMenu,
    onClick,
    className = "",
}: DisplayMenuCardProps) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`overflow-hidden rounded-2xl bg-white text-left shadow-[0_1px_2px_rgba(0,0,0,0.04),0_6px_16px_-4px_rgba(0,0,0,0.08)] transition-[box-shadow,transform] duration-200 hover:-translate-y-0.5 hover:shadow-[0_2px_4px_rgba(0,0,0,0.05),0_12px_28px_-6px_rgba(0,0,0,0.12)] active:scale-[0.98] ${className}`}
        >
            <div className="aspect-[4/3] bg-gray-100">
                {displayMenu.image ? (
                    <img
                        src={displayMenu.image}
                        alt={displayMenu.title}
                        className="h-full w-full object-cover outline outline-1 -outline-offset-1 outline-black/10"
                    />
                ) : (
                    <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-orange-50 via-white to-amber-50 px-4 text-center">
                        <span className="text-sm font-black text-orange-300">
                            MENU
                        </span>
                    </div>
                )}
            </div>

            <div className="space-y-2 p-4">
                <h3 className="line-clamp-1 text-[15px] font-black text-gray-900">
                    {displayMenu.title}
                </h3>
                {displayMenu.description ? (
                    <p className="line-clamp-2 text-[12px] leading-relaxed text-gray-700">
                        {displayMenu.description}
                    </p>
                ) : (
                    <p className="text-[12px] text-gray-300">설명이 없습니다.</p>
                )}
            </div>
        </button>
    );
}

interface DisplayMenuDetailModalProps {
    displayMenu: DisplayMenu | null;
    onClose: () => void;
}

export function DisplayMenuDetailModal({
    displayMenu,
    onClose,
}: DisplayMenuDetailModalProps) {
    if (!displayMenu) {
        return null;
    }

    return (
        <Modal isOpen={!!displayMenu} toggle={onClose} variant="full" className="gap-0 border-0 bg-white">
            <div className="flex min-h-full flex-col">
                <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex h-8 w-8 items-center justify-center rounded-full text-gray-700 hover:bg-gray-100"
                        aria-label="Back"
                    >
                        <ChevronLeft size={18} />
                    </button>
                    <h2 className="text-base font-semibold text-gray-900">메뉴 안내</h2>
                </div>

                <div className="flex-1 overflow-y-auto pb-8">
                    <div className="aspect-[4/3] bg-gray-100">
                        {displayMenu.image ? (
                            <img
                                src={displayMenu.image}
                                alt={displayMenu.title}
                                className="h-full w-full object-cover"
                            />
                        ) : (
                            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-orange-50 via-white to-amber-50">
                                <span className="text-lg font-black tracking-[0.3em] text-orange-200">
                                    MENU
                                </span>
                            </div>
                        )}
                    </div>

                    <div className="space-y-4 p-6">
                        {/* <div className="inline-flex rounded-full bg-cyan/10 px-3 py-1 text-[11px] font-bold -poppins">
                            Smart Landing Menu
                        </div> */}
                        <h3 className="text-2xl font-black leading-tight text-gray-900 text-balance">
                            {displayMenu.title}
                        </h3>
                        {displayMenu.description ? (
                            <p className="whitespace-pre-wrap text-sm leading-7 text-gray-700 text-pretty">
                                {displayMenu.description}
                            </p>
                        ) : (
                            <p className="text-sm text-gray-700">
                                등록된 설명이 없습니다.
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </Modal>
    );
}

interface DisplayMenuHorizontalListProps {
    displayMenus: DisplayMenu[];
    onSelect: (displayMenu: DisplayMenu) => void;
}

export function DisplayMenuHorizontalList({
    displayMenus,
    onSelect,
}: DisplayMenuHorizontalListProps) {
    return (
        <div className="scrollbar-hide -mx-5 overflow-x-auto px-5">
            <div className="flex w-max gap-4 pb-2 pr-5">
                {displayMenus.map((displayMenu) => (
                    <DisplayMenuCard
                        key={displayMenu.id}
                        displayMenu={displayMenu}
                        onClick={() => onSelect(displayMenu)}
                        className="w-[220px] shrink-0"
                    />
                ))}
            </div>
        </div>
    );
}

interface DisplayMenuGridListProps {
    displayMenus: DisplayMenu[];
    onSelect: (displayMenu: DisplayMenu) => void;
}

export function DisplayMenuGridList({
    displayMenus,
    onSelect,
}: DisplayMenuGridListProps) {
    return (
        <div className="grid grid-cols-1 gap-4">
            {displayMenus.map((displayMenu) => (
                <DisplayMenuCard
                    key={displayMenu.id}
                    displayMenu={displayMenu}
                    onClick={() => onSelect(displayMenu)}
                />
            ))}
        </div>
    );
}

export function DisplayMenuHorizontalSkeleton() {
    return (
        <div className="-mx-5 overflow-x-auto px-5">
            <div className="flex w-max gap-4 pr-5">
                {[1, 2, 3].map((item) => (
                    <div
                        key={item}
                        className="w-[220px] shrink-0 overflow-hidden rounded-2xl bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_6px_16px_-4px_rgba(0,0,0,0.08)]"
                    >
                        <Skeleton className="aspect-[4/3] w-full rounded-none" />
                        <div className="space-y-2 p-4">
                            <Skeleton className="h-4 w-2/3" />
                            <Skeleton className="h-3 w-full" />
                            <Skeleton className="h-3 w-4/5" />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export function DisplayMenuGridSkeleton() {
    return (
        <div className="grid grid-cols-1 gap-4">
            {[1, 2, 3].map((item) => (
                <div
                    key={item}
                    className="overflow-hidden rounded-2xl bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_6px_16px_-4px_rgba(0,0,0,0.08)]"
                >
                    <Skeleton className="aspect-[4/3] w-full rounded-none" />
                    <div className="space-y-2 p-4">
                        <Skeleton className="h-4 w-2/3" />
                        <Skeleton className="h-3 w-full" />
                        <Skeleton className="h-3 w-4/5" />
                    </div>
                </div>
            ))}
        </div>
    );
}
